import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useDatasheetTypes, useUpdateDatasheet,
  type DatasheetDetail, type UpdateDatasheetBody,
} from "@/lib/datasheets";
import { describeMode } from "../mode-description";

export function OverviewSection({ sheet }: { sheet: DatasheetDetail }) {
  const { has } = usePermissions();
  const update = useUpdateDatasheet(sheet.datasheetId);
  const { data: types } = useDatasheetTypes();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [typeCode, setTypeCode] = useState(sheet.datasheetTypeCode);
  const [department, setDepartment] = useState(sheet.department ?? "");
  const [isPrepaid, setIsPrepaid] = useState(sheet.isPrepaid);
  const [clientStock, setClientStock] = useState(sheet.clientSuppliedStock);
  const [turnaround, setTurnaround] = useState(sheet.specialTurnaroundDays?.toString() ?? "");
  const [note, setNote] = useState(sheet.note ?? "");

  function resetForm() {
    setTypeCode(sheet.datasheetTypeCode);
    setDepartment(sheet.department ?? "");
    setIsPrepaid(sheet.isPrepaid);
    setClientStock(sheet.clientSuppliedStock);
    setTurnaround(sheet.specialTurnaroundDays?.toString() ?? "");
    setNote(sheet.note ?? "");
  }

  async function save() {
    setSaving(true);
    try {
      const body: UpdateDatasheetBody = {
        datasheetTypeCode: typeCode, department, isPrepaid, clientSuppliedStock: clientStock,
        specialTurnaroundDays: turnaround === "" ? null : Number(turnaround), note,
      };
      await update.mutateAsync({ body, rowVersion: sheet.rowVersionBase64 });
      toast.success("Saved");
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 409
        ? "Changed since you opened it — reload."
        : "Could not save the datasheet");
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="space-y-2 text-sm">
        <Row label="Type" value={sheet.datasheetTypeDescription ?? sheet.datasheetTypeCode} />
        <Row label="Department" value={sheet.department ?? "—"} />
        <Row label="Mode" value={describeMode(sheet.isPrepaid, sheet.clientSuppliedStock)} />
        <Row label="Special turnaround days" value={sheet.specialTurnaroundDays?.toString() ?? "—"} />
        <Row label="Note" value={sheet.note ?? "—"} />
        {has("datasheet:write") ? (
          <Button size="sm" variant="outline" onClick={() => { resetForm(); setEditing(true); }}>Edit</Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-3">
      <div className="space-y-1">
        <Label htmlFor="ov-type">Type</Label>
        <select id="ov-type" aria-label="Type" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
          {(types ?? []).map((t) => <option key={t.datasheetTypeCode} value={t.datasheetTypeCode}>{t.description}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="ov-dept">Department</Label>
        <Input id="ov-dept" value={department} onChange={(e) => setDepartment(e.target.value)} />
      </div>
      <fieldset className="space-y-2 rounded-md border p-3">
        <legend className="px-1 text-sm font-medium">Mode</legend>
        <label htmlFor="ov-prepaid" className="flex items-center gap-2 text-sm">
          <Checkbox id="ov-prepaid" checked={isPrepaid} onCheckedChange={(v) => setIsPrepaid(v === true)} aria-label="Prepaid" /> Prepaid
        </label>
        <label htmlFor="ov-clientstock" className="flex items-center gap-2 text-sm">
          <Checkbox id="ov-clientstock" checked={clientStock} onCheckedChange={(v) => setClientStock(v === true)} aria-label="Client-supplied stock" /> Client-supplied stock
        </label>
        <p className="text-xs text-muted-foreground">{describeMode(isPrepaid, clientStock)}</p>
      </fieldset>
      <div className="space-y-1">
        <Label htmlFor="ov-turn">Special turnaround days</Label>
        <Input id="ov-turn" type="number" value={turnaround} onChange={(e) => setTurnaround(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ov-note">Note</Label>
        <Input id="ov-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>Save</Button>
        <Button size="sm" variant="outline" onClick={() => { resetForm(); setEditing(false); }}>Cancel</Button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-48 text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
